import { create } from 'zustand';

/**
 * UI Store
 * Manages UI state like active tabs, modals, notifications
 */
export const useUIStore = create((set, get) => ({
  // State
  activeTab: 'dashboard',
  notifications: [],
  selectedProjectId: null,
  modals: {
    addProject: false,
    createProject: false,
    editProject: false,
    deleteProject: false,
    addEmployee: false,
    editEmployee: false,
    createEmployee: false,
    linkClient: false
  },
  
  // Actions
  setActiveTab: (tab) => set({ activeTab: tab }),
  setSelectedProjectId: (projectId) => set({ selectedProjectId: projectId }),
  
  // Notification management
  addNotification: (notification) => {
    const id = Date.now().toString();
    const newNotification = {
      id,
      type: 'info', // info, success, warning, error
      title: '',
      message: '',
      duration: 5000, // 5 seconds default
      ...notification
    };
    
    set(state => ({
      notifications: [...state.notifications, newNotification]
    }));
    
    // Auto remove notification after duration
    if (newNotification.duration > 0) {
      setTimeout(() => {
        get().removeNotification(id);
      }, newNotification.duration);
    }
    
    return id;
  },
  
  removeNotification: (id) => {
    set(state => ({
      notifications: state.notifications.filter(n => n.id !== id)
    }));
  },
  
  clearNotifications: () => set({ notifications: [] }),
  
  // Modal management
  openModal: (modalName) => {
    set(state => ({
      modals: {
        ...state.modals,
        [modalName]: true
      }
    }));
  },
  
  closeModal: (modalName) => {
    set(state => ({
      modals: {
        ...state.modals,
        [modalName]: false
      }
    }));
  },
  
  closeAllModals: () => {
    set(state => ({
      modals: Object.keys(state.modals).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {})
    }));
  },
  
  // Utility methods
  showSuccessNotification: (message, title = 'Success') => {
    return get().addNotification({
      type: 'success',
      title,
      message
    });
  },
  
  showErrorNotification: (message, title = 'Error') => {
    return get().addNotification({
      type: 'error',
      title,
      message,
      duration: 8000 // Longer duration for errors
    });
  },
  
  showInfoNotification: (message, title = 'Info') => {
    return get().addNotification({
      type: 'info',
      title,
      message
    });
  },
  
  showWarningNotification: (message, title = 'Warning') => {
    return get().addNotification({
      type: 'warning',
      title,
      message,
      duration: 6000
    });
  }
}));